/**
 * Support/Feedback form utility functions and types.
 *
 * Provides word counting, form validation, and shared interfaces
 * used by SupportFormDialog and the support service layer.
 */

export interface SupportFormData {
  type: 'Issue' | 'Feedback';
  text: string;
  outfitId?: string;
}

export interface SupportSubmitPayload {
  type: 'Issue' | 'Feedback';
  text: string;
  outfitId?: string;
  userId?: string;
  userEmail?: string;
  userDisplayName?: string;
  username?: string;
}

export interface SupportSubmitResponse {
  success: boolean;
  message?: string;
  error?: {
    code: string;
    message: string;
  };
}

export interface SupportFormValidation {
  valid: boolean;
  errors: {
    text?: string;
    type?: string;
  };
}

const MAX_WORDS = 600;

/**
 * Count the number of whitespace-separated, non-empty tokens in a string.
 * Returns 0 for empty or whitespace-only strings.
 */
export function countWords(text: string): number {
  if (!text) return 0;
  const tokens = text.trim().split(/\s+/);
  // After trim + split, a whitespace-only string produces ['']
  if (tokens.length === 1 && tokens[0] === '') return 0;
  return tokens.length;
}

/**
 * Validate support form data.
 * - type must be 'Issue' or 'Feedback'
 * - text must contain between 1 and 600 words (inclusive)
 */
export function validateSupportForm(data: {
  type: string;
  text: string;
}): SupportFormValidation {
  const errors: SupportFormValidation['errors'] = {};

  if (!data.type || (data.type !== 'Issue' && data.type !== 'Feedback')) {
    errors.type = 'Please select a type (Issue or Feedback)';
  }

  const wordCount = countWords(data.text);
  if (wordCount === 0) {
    errors.text = 'Please enter a description';
  } else if (wordCount > MAX_WORDS) {
    errors.text = `Your message exceeds the ${MAX_WORDS} word limit (${wordCount} words)`;
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
